import { useState, useCallback, useEffect } from 'react';
import { useCurrentUser } from '../../../../Session';
import {
    DownloadFileType,
    FullPurchaseTransactionDataFragment,
    GetPurchaseTransactionQueryResult,
    GetQuotationQueryResult,
    QuotationUpdatedSubscription,
    useGetPurchaseTransactionLiveDisplayQuery,
    useGetQuotationQuery,
    UserType,
} from '../../../../api';
import FileViewerProvider from '../../../../components/FileViewerProvider';
import { useLoading } from '../../../../components/LoadingProvider';
import { DownloadDetailFn } from '../../components';
import AcceptValuationConfirmDialog from './AcceptValuationConfirmDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import CompleteTransactionDrawer from './CompleteTransactionDrawer';
import ExportPurchaseTransactionPdfDialog, {
    PurchaseTransactionPdfOptions,
} from './ExportPurchaseTransactionPdfDialog';
import ViewPurchaseTransactionBody from './ViewPurchaseTransactionBody';
import ViewPurchaseTransactionHeader from './ViewPurchaseTransactionHeader';

export type ViewPurchaseTransactionProps = {
    purchaseTransaction: FullPurchaseTransactionDataFragment;
    quotationUpdated?: QuotationUpdatedSubscription['quotationUpdated'];
    goToEdition: () => void;
    goToVehicleDetails: () => void;
    downloadPurchaseTransactionDetailFile: DownloadDetailFn;
    refetchPurchaseTransaction: GetPurchaseTransactionQueryResult['refetch'];
};

const ViewPurchaseTransaction = ({
    purchaseTransaction,
    quotationUpdated,
    goToEdition,
    goToVehicleDetails,
    downloadPurchaseTransactionDetailFile,
    refetchPurchaseTransaction,
}: ViewPurchaseTransactionProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [openCompleteDrawer, setOpenCompleteDrawer] = useState(false);
    const [openCancelTransactionDialog, setOpenCancelTransactionDialog] = useState(false);
    const [openPdfExportDialog, setOpenPdfExportDialog] = useState(false);
    const { attach } = useLoading();
    const currentUser = useCurrentUser();
    const displayQuotation = [UserType.Approver, UserType.ValuationTeam, UserType.Admin].includes(currentUser.type);
    const quotationQuery: GetQuotationQueryResult = useGetQuotationQuery({
        fetchPolicy: 'cache-and-network',
        variables: { purchaseTransactionId: purchaseTransaction.id },
        skip: !displayQuotation,
    });
    const quotation = quotationQuery.data?.quotation;
    const { refetch: refetchQuotation } = quotationQuery;
    const liveDisplayQuery = useGetPurchaseTransactionLiveDisplayQuery({
        fetchPolicy: 'no-cache',
        variables: { purchaseTransactionId: purchaseTransaction.id },
        skip: !displayQuotation,
    });
    const liveQuotation = liveDisplayQuery.data?.quotation;
    const { refetch: refetchLiveDisplay } = liveDisplayQuery;

    useEffect(() => {
        if (quotationUpdated?.quotationEntryAdded) {
            Promise.all([refetchQuotation(), refetchLiveDisplay()]).catch(() => undefined);
        }
    }, [quotationUpdated, refetchLiveDisplay, refetchQuotation]);

    const downloadFile = useCallback(
        (type: DownloadFileType, pdfOptions?: PurchaseTransactionPdfOptions) => {
            const execute = async () => {
                await downloadPurchaseTransactionDetailFile({
                    downloadFileType: type,
                    transactionId: purchaseTransaction.id,
                    ...(type === DownloadFileType.Pdf ? pdfOptions : {}),
                });
            };

            attach(execute());
        },
        [attach, downloadPurchaseTransactionDetailFile, purchaseTransaction.id]
    );

    return (
        <FileViewerProvider>
            <AcceptValuationConfirmDialog
                purchaseTransaction={purchaseTransaction}
                setHideConfirmDialog={() => setShowConfirmDialog(false)}
                show={showConfirmDialog}
            />
            <CancelTransactionDialog
                onClose={() => setOpenCancelTransactionDialog(false)}
                open={openCancelTransactionDialog}
                purchaseTransaction={purchaseTransaction}
                refetchQuotation={quotationQuery.refetch}
            />
            <CompleteTransactionDrawer
                onClose={() => setOpenCompleteDrawer(false)}
                open={openCompleteDrawer}
                purchaseTransaction={purchaseTransaction}
            />
            <ExportPurchaseTransactionPdfDialog
                defaultHideVehicleNo={quotation?.hideVehicleNo ?? false}
                onClose={() => setOpenPdfExportDialog(false)}
                onExport={options => downloadFile(DownloadFileType.Pdf, options)}
                open={openPdfExportDialog}
            />
            <ViewPurchaseTransactionHeader
                downloadExcel={() => downloadFile(DownloadFileType.Excel)}
                downloadPdf={() => setOpenPdfExportDialog(true)}
                goToEdition={goToEdition}
                purchaseTransaction={purchaseTransaction}
            />
            <ViewPurchaseTransactionBody
                displayQuotation={displayQuotation}
                goToVehicleDetails={goToVehicleDetails}
                liveQuotation={liveQuotation}
                openCancelTransactionDialog={() => setOpenCancelTransactionDialog(true)}
                openCompleteDrawer={() => setOpenCompleteDrawer(true)}
                purchaseTransaction={purchaseTransaction}
                quotation={quotation}
                quotationLoading={quotationQuery.loading}
                refetchLiveQuotation={refetchLiveDisplay}
                refetchPurchaseTransaction={refetchPurchaseTransaction}
                refetchQuotation={refetchQuotation}
                showConfirmDialog={() => setShowConfirmDialog(true)}
            />
        </FileViewerProvider>
    );
};

export default ViewPurchaseTransaction;
